import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Building2, Users, X, Lock, Calendar } from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";
import { bookingUrl, isExternalBooking } from "../lib/bookingUrl";
import { useTrackOnMount } from "../lib/analytics";

const PERKS = {
  starter: [
    "Portfolio Command Center · Executive Scorecard · S/4HANA cockpit",
    "Dashboard · Tracker · Requirements · Documents",
    "RAID · Actions · Budget · Stakeholders · Gap/Fit",
    "PDF · Word · PPT exports (with capittance.com branding)",
    "Capittance NAVBOT — executive AI · chat mode (Claude 4.5)",
    "Full autosave · shareable links · email-code verification",
  ],
  pro: [
    "Everything in Starter — plus Platinum tier",
    "Microsoft Dynamics 365 executive cockpit (Finance · Sales · SCM · CX)",
    "Oracle Cloud ERP executive cockpit (Financials · SCM · HCM)",
    "Value Realization Tracker · AI Insight Feed · Board Pack generator",
    "Governance · Fit-to-Standard · Data Migration · Integration · Testing · OCM · Cutover",
    "S/4HANA (134-step Activate) · Strategy framework · IP Library",
    "Capittance NAVBOT — proactive briefings + quick actions",
    "Teams webhooks · 3-tier escalation · priority support",
    "Live D365 / S/4HANA / Oracle connectors",
    "AI-generated SAP Activate document pipeline",
  ],
};

const DOMAINS = [
  { name: "Governance",            tier: "Starter",  body: "Steering committees, RACI matrices, decision logs, escalation ladders and audit trails." },
  { name: "Fit-to-Standard",       tier: "Starter",  body: "Workshop-driven gap analysis, process fit scoring, deviation register." },
  { name: "Data Migration",        tier: "Platinum", body: "Object catalogue, cleansing scorecards, mock-load cycle tracking, reconciliation." },
  { name: "Integration",           tier: "Platinum", body: "Interface register with owner, protocol, middleware, test status." },
  { name: "Testing",               tier: "Starter",  body: "SIT / UAT / performance / regression waves, defect triage, entry/exit criteria." },
  { name: "OCM & Training",        tier: "Platinum", body: "Change-impact heatmap, stakeholder journey mapping, adoption metrics." },
  { name: "Cutover & Hypercare",   tier: "Platinum", body: "Minute-by-minute runbook, dress rehearsals, hypercare command centre." },
  { name: "Value Realization",     tier: "Platinum", body: "Planned vs achieved benefits, benefit owners, board-ready value dashboards." },
  { name: "Reusable IP",           tier: "Starter",  body: "Templates (charters, RAIDs, cutover packs), accelerators, lessons learned." },
  { name: "AI Layer",              tier: "Platinum", body: "Natural-language queries, AI-drafted status, RAID summarisation, board-update generation." },
];

export default function PricingPage() {
  const [openDomain, setOpenDomain] = useState(null);
  useSEO({
    title: "Pricing · Capittance Harmonizer — $200 Starter · $350 Platinum",
    description: "Two tiers. Cancel anytime. NAVBOT executive AI, Board Pack generator, D365 + S/4HANA + Oracle Cloud ERP cockpits, 10 delivery domains. Private per-tenant workspace. From $6.60/day per user.",
    path: "/pricing",
  });
  useTrackOnMount("pricing_viewed");
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Pricing" subtitle="Simple. Two tiers. Cancel anytime." />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="page-pricing">
        <div className="text-center mb-10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Capittance · Harmonizer</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mt-2">Delivery platform pricing</h1>
          <p className="text-sm text-zinc-600 mt-3 max-w-2xl mx-auto">
            Choose the tier that fits your team. Both include the operator workspace, NAVBOT (Claude 4.5 executive AI), the Board Pack generator, and a private per-tenant workspace. Platinum unlocks live D365, S/4HANA and Oracle Cloud ERP connectors plus every delivery domain.
          </p>
          <div className="mt-6 inline-flex flex-col items-center gap-2">
            <a
              href={bookingUrl("walkthrough")}
              {...(isExternalBooking() ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="btn btn-primary inline-flex items-center gap-2"
              data-testid="pricing-book-walkthrough"
            >
              <Calendar size={14}/> Book a Board Pack Walkthrough
            </a>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              30-min live demo · CIO / CFO cohort onboarding
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          <PlanCard
            plan="starter"
            title="Starter · Basic Portal"
            price={200}
            audience="PM operators · single project team"
            perks={PERKS.starter}
            cta="Book a Board Pack Walkthrough"
          />
          <PlanCard
            plan="pro"
            title="Platinum · Executive Portal"
            price={350}
            audience="CIOs · CFOs · portfolio owners"
            perks={PERKS.pro}
            featured
            pilot
            cta="Book a Board Pack Walkthrough"
          />
        </div>

        <div className="mt-12">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Included modules · 10 SAP delivery domains</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">What each subscription unlocks</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {DOMAINS.map((d, i) => (
              <button key={d.name} onClick={() => setOpenDomain(d)} className="card p-4 text-left hover:shadow-md transition-shadow" data-testid={`pricing-domain-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-display text-base font-bold tracking-tight">{d.name}</div>
                  <span className={`pill text-[10px] ${d.tier === "Platinum" ? "pill-blue" : "pill-neutral"}`}>{d.tier}</span>
                </div>
                <div className="text-sm text-zinc-600 leading-relaxed">{d.body}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-cyan-700">Learn more →</div>
              </button>
            ))}
          </div>
        </div>

        <FAQ />
        {openDomain && <DomainModal d={openDomain} onClose={() => setOpenDomain(null)} />}
        <LegalFooter />
      </main>
    </div>
  );
}

function PlanCard({ plan, title, price, audience, perks, featured, cta, pilot }) {
  return (
    <div className={`card p-6 relative ${featured ? "border-2" : ""}`} style={featured ? { borderColor: "#0369A1" } : {}} data-testid={`plan-card-${plan}`}>
      {featured && <div className="absolute -top-2 right-4 pill pill-blue text-[9px]">MOST POPULAR</div>}
      {pilot && <div className="absolute -top-2 left-4 pill text-[9px]" style={{ background: "#FEF3C7", color: "#78350F", border: "1px solid #F59E0B44" }} data-testid="pilot-badge">14-DAY PILOT · $0 TODAY</div>}
      <div className="flex items-center gap-2 mb-1">
        {plan === "pro" ? <Sparkles size={14} className="text-cyan-600"/> : <Building2 size={14} className="text-sky-700"/>}
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{plan === "pro" ? "Platinum" : "Starter"}</div>
      </div>
      <div className="font-display text-2xl font-extrabold tracking-tight text-zinc-900">{title}</div>
      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Users size={11}/> {audience}</div>
      {pilot ? (
        <>
          <div className="my-5">
            <div className="flex items-baseline gap-2">
              <div className="font-display text-5xl font-extrabold text-zinc-900">$0</div>
              <div className="font-mono text-xs text-zinc-500">today · 14-day pilot</div>
            </div>
            <div className="mt-1 font-mono text-[11px] text-zinc-600">
              then <b className="text-zinc-900">${price}/mo</b> per user · cancel before day 14 to avoid the charge
            </div>
          </div>
          <div className="-mt-3 mb-4 font-mono text-[10px] uppercase tracking-widest text-cyan-700">
            After conversion: ${(price / 30).toFixed(2)}/day
          </div>
        </>
      ) : (
        <>
          <div className="my-5 flex items-baseline gap-2">
            <div className="font-display text-5xl font-extrabold text-zinc-900">${price}</div>
            <div className="font-mono text-xs text-zinc-500">/mo · per user · billed monthly</div>
          </div>
          <div className="-mt-3 mb-4 font-mono text-[10px] uppercase tracking-widest text-cyan-700">
            That&rsquo;s ${(price / 30).toFixed(2)}/day · less than one coffee</div>
        </>
      )}
      <ul className="text-sm text-zinc-700 space-y-2 mb-6">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={14} className="mt-0.5 text-emerald-600 shrink-0"/>
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mb-3 rounded-sm border border-dashed border-cyan-300 bg-cyan-50/60 px-3 py-2 text-[11px] text-cyan-900 font-mono" data-testid={`pricing-pilot-note-${plan}`}>
        <b>Custom pilot terms</b> available for first cohort — negotiated during your walkthrough.
      </div>
      <a
        href={bookingUrl(plan === "pro" ? "executive-walkthrough" : "walkthrough")}
        {...(isExternalBooking() ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className={`btn ${featured ? "btn-primary" : "btn-secondary"} w-full justify-center inline-flex items-center gap-1.5`}
        data-testid={`pricing-cta-${plan}`}
      >
        <Calendar size={14}/> {cta} <ArrowRight size={14}/>
      </a>
      <div className="mt-3 text-center">
        <Link
          to={`/signup?plan=${plan}`}
          className="text-[11px] font-mono text-zinc-400 hover:text-zinc-600 underline underline-offset-2"
          data-testid={`pricing-signup-fallback-${plan}`}
        >
          Prefer to self-serve? Sign up directly →
        </Link>
      </div>
      <div className="mt-2 text-[10px] font-mono text-zinc-400 text-center">
        {pilot ? "Card required on self-serve · not charged until day 15 · one-click cancel" : "Cancel any time · non-refundable · no proration"}
      </div>
    </div>
  );
}

function FAQ() {
  const faqs = [
    { q: "How does the Platinum pilot work?", a: "Platinum starts with a 14-day pilot at $0. You add a card at signup — it activates full Platinum access instantly (NAVBOT, Board Pack, D365/S4HANA/Oracle cockpits, AI Insights). No charge is taken during the pilot. On day 15 the card is charged $350 and the account converts to a standard monthly Platinum subscription — unless you cancel from your dashboard before then. One-click cancel from the C-Suite settings, no phone calls, no retention gauntlet." },
    { q: "What happens if I cancel before day 14?", a: "Zero charge. Your account downgrades to the free Starter tier so you keep all the RAID logs, projects and exports you built during the pilot. You can resubscribe at any time." },
    { q: "Can I switch tiers later?", a: "Yes. Upgrades take effect immediately (you're charged the price difference on the next cycle). Downgrades take effect at the next renewal." },
    { q: "Does Starter have a pilot too?", a: "No. Starter is $200/month, cancel any time. Month-to-month, non-refundable, no proration. The pilot is a Platinum-only mechanic because CIOs need a scoped evaluation window; PM operators just want to start work." },
    { q: "What data does NAVBOT see?", a: "Your rollup data (project names, KPIs, risks, actions) is used to build the prompt — and only your tenant's data. NAVBOT is available exclusively inside the C-Suite portal and never sees data from other Capittance customers. Conversations are stored so the assistant maintains multi-turn context. Delete anytime from Settings." },
    { q: "Can I connect my own Dynamics 365 / S/4HANA / Oracle Cloud?", a: "Yes, on Platinum. The Connections tab accepts Azure AD app credentials for D365, OData basic auth for S/4HANA, and OAuth 2.0 client credentials for Oracle Cloud ERP. Secrets never leave the server." },
    { q: "Do you offer enterprise pricing?", a: "Yes — for teams over 25 users, email Requests@Capittance.com for a custom quote with SSO, DPA, and annual billing. Enterprise pilots run 30 days by default." },
  ];
  return (
    <div className="mt-14">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Frequently asked</div>
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4">Common questions</h2>
      <div className="space-y-4">
        {faqs.map((f, i) => (
          <details key={i} className="card p-4 group" data-testid={`faq-${i}`}>
            <summary className="cursor-pointer font-display text-sm font-bold tracking-tight text-zinc-900 marker:content-['']">
              <span className="mr-2 text-cyan-600 group-open:rotate-90 inline-block transition-transform">›</span>
              {f.q}
            </summary>
            <div className="text-sm text-zinc-700 mt-2 pl-5 leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
    </div>
  );
}

function DomainModal({ d, onClose }) {
  const isPro = d.tier === "Platinum";
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-6 md:p-8 relative" style={{ borderTop: "4px solid #0369A1" }} onClick={(e) => e.stopPropagation()} data-testid="pricing-domain-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-800"><X size={18}/></button>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SAP Delivery Domain · {isPro ? "Platinum tier" : "Starter tier"}</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1">{d.name}</h2>
        <p className="text-sm text-zinc-700 leading-relaxed mt-4">{d.body}</p>
        <div className="mt-5 flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-[var(--border)] rounded-sm p-3">
          <Lock size={14} className="mt-0.5 shrink-0 text-zinc-400"/>
          <span>Not functional until you subscribe. {isPro ? "Available on Platinum ($350/mo)." : "Available on Starter ($200/mo) and Platinum ($350/mo)."}</span>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          <Link to={`/signup?plan=${isPro ? "pro" : "starter"}`} className="text-[11px] font-mono text-zinc-400 hover:text-zinc-600 underline underline-offset-2" data-testid="pricing-modal-signup-fallback">
            Sign up directly →
          </Link>
          <a href={bookingUrl(isPro ? "executive-walkthrough" : "walkthrough")} {...(isExternalBooking() ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="pricing-modal-book-walkthrough">
            <Calendar size={13}/> Book walkthrough <ArrowRight size={13}/>
          </a>
        </div>
      </div>
    </div>
  );
}
